import React, { useState } from 'react';

// Sacred Tech palette
const C = {
  emerald: '#064E3B', emeraldMid: '#065F46',
  emeraldDim: 'rgba(6,78,59,.08)',
  gold: '#D4AF37', goldDim: 'rgba(212,175,55,.16)',
  mist: '#1A202C', mist2: '#2D3748', mist3: '#4A5568', mist4: '#718096',
};

const QUALITY_STYLE = {
  '大吉':      { bar: '#059669', card: 'rgba(5,150,105,.06)',  border: 'rgba(5,150,105,.25)',  text: '#064E3B' },
  'Excellent': { bar: '#059669', card: 'rgba(5,150,105,.06)',  border: 'rgba(5,150,105,.25)',  text: '#064E3B' },
  '吉':        { bar: '#064E3B', card: 'rgba(6,78,59,.05)',    border: 'rgba(6,78,59,.20)',    text: '#064E3B' },
  'Good':      { bar: '#064E3B', card: 'rgba(6,78,59,.05)',    border: 'rgba(6,78,59,.20)',    text: '#064E3B' },
  '平':        { bar: '#94A3B8', card: 'rgba(148,163,184,.06)',border: 'rgba(148,163,184,.25)',text: '#4A5568' },
  'Neutral':   { bar: '#94A3B8', card: 'rgba(148,163,184,.06)',border: 'rgba(148,163,184,.25)',text: '#4A5568' },
  '凶':        { bar: '#D97706', card: 'rgba(217,119,6,.06)',  border: 'rgba(217,119,6,.25)',  text: '#92400E' },
  'Challenging':{ bar: '#D97706',card: 'rgba(217,119,6,.06)',  border: 'rgba(217,119,6,.25)',  text: '#92400E' },
  '大凶':      { bar: '#DC2626', card: 'rgba(220,38,38,.06)',  border: 'rgba(220,38,38,.22)',  text: '#7F1D1D' },
  'Difficult': { bar: '#DC2626', card: 'rgba(220,38,38,.06)',  border: 'rgba(220,38,38,.22)',  text: '#7F1D1D' },
};

// Map heavenly stem (天干) to element color
const STEM_ELEMENT = {
  '甲':'Wood', '乙':'Wood', '丙':'Fire', '丁':'Fire', '戊':'Earth',
  '己':'Earth', '庚':'Metal', '辛':'Metal', '壬':'Water', '癸':'Water'
};
const ELEMENT_COLOR = {
  Fire:  { main:'#DC2626', warm:'#FCA5A5', glow:'rgba(220,38,38,.15)' },
  Wood:  { main:'#16A34A', warm:'#86EFAC', glow:'rgba(22,163,74,.15)' },
  Water: { main:'#2563EB', warm:'#93C5FD', glow:'rgba(37,99,235,.15)' },
  Earth: { main:'#D4AF37', warm:'#FDE68A', glow:'rgba(212,175,55,.20)' },
  Metal: { main:'#78716C', warm:'#D6D3D1', glow:'rgba(120,113,108,.12)' },
};

const MONTH_ZH = ['正','二','三','四','五','六','七','八','九','十','冬','腊'];

function getStemElement(ganzhi) {
  const stem = ganzhi ? ganzhi[0] : null;
  return STEM_ELEMENT[stem] || 'Earth';
}

// Semicircular barometer gauge
function Barometer({ score, element, size = 44 }) {
  const ec = ELEMENT_COLOR[element] || ELEMENT_COLOR.Earth;
  const r = 15;
  const cx = size / 2;
  const cy = size * 0.72;
  const pct = Math.min(1, Math.max(0, score / 100));
  // Arc from 180deg (left) to 0deg (right)
  const angle = Math.PI - pct * Math.PI;
  const nx = cx + r * Math.cos(angle);
  const ny = cy - r * Math.sin(angle);  // SVG y is inverted
  // Background arc path (half circle)
  const bgArc = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;
  // Colored fill arc
  const fillArc = pct > 0.001
    ? `M ${cx - r} ${cy} A ${r} ${r} 0 ${pct > 0.5 ? 1 : 0} 1 ${nx} ${ny}`
    : '';
  return (
    <svg width={size} height={size * 0.72} viewBox={`0 0 ${size} ${size * 0.72}`} style={{display:'block'}}>
      {/* Glow */}
      {score >= 70 && <circle cx={cx} cy={cy} r={r + 4} fill={ec.glow} />}
      {/* Background arc */}
      <path d={bgArc} fill="none" stroke="#E2E8F0" strokeWidth={4} strokeLinecap="round" />
      {/* Filled arc */}
      {fillArc && <path d={fillArc} fill="none" stroke={ec.main} strokeWidth={4} strokeLinecap="round" />}
      {/* Needle */}
      <line x1={cx} y1={cy} x2={nx} y2={ny} stroke={ec.main} strokeWidth={1.8} strokeLinecap="round" />
      {/* Pivot */}
      <circle cx={cx} cy={cy} r={3} fill={ec.main} />
      {/* Score text */}
      <text x={cx} y={cy - r - 3} textAnchor="middle" fontSize={9} fill={ec.main} fontWeight="700">{score}</text>
    </svg>
  );
}

const MonthlyTimeline = ({ monthly_fortunes, monthlyFortune, lang = 'en' }) => {
  const [expanded, setExpanded] = useState(null);
  const data = monthly_fortunes || monthlyFortune;

  if (!data || data.length === 0) return null;
  // alias for legacy code below
  const monthlyFortune_ = data;

  const isZh = lang === 'zh';

  const t = (mf, field) => mf[`${field}_en`] !== undefined && !isZh
    ? mf[`${field}_en`]
    : mf[field];

  const title        = isZh ? '2026年月运走势' : '2026 Monthly Fortune';
  const luckyLabel   = isZh ? '吉日' : 'Lucky Days';
  const cautionLabel = isZh ? '忌日' : 'Caution Days';
  const focusLabel   = isZh ? '重点' : 'Focus';

  return (
    <div style={{ marginTop: 32, background: 'rgba(255,255,255,.82)', border: '1px solid rgba(212,175,55,.16)', backdropFilter: 'blur(12px)', borderRadius: 16, padding: '28px 32px' }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, letterSpacing: '0.18em', color: C.mist4, textTransform: 'uppercase', marginBottom: 4 }}>
          {isZh ? '丙午年 · 十二月运程' : 'Year of Bingwu · Twelve Months'}
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: C.emerald }}>📅 {title}</div>
      </div>

      {/* Energy barometer sparkline */}
      <div style={{
        display: 'flex', alignItems: 'flex-end', gap: 3,
        background: 'rgba(249,247,242,.80)', border: '1px solid rgba(212,175,55,.14)',
        borderRadius: 10, padding: '16px 16px 10px', marginBottom: 24,
        overflowX: 'auto',
      }}>
        {monthlyFortune_.map((mf) => {
          const qualityKey = isZh ? mf.quality : (mf.quality_en || mf.quality);
          const qs = QUALITY_STYLE[qualityKey] || QUALITY_STYLE['平'];
          const elem = getStemElement(mf.ganzhi);
          const ec = ELEMENT_COLOR[elem] || ELEMENT_COLOR.Earth;
          const isOpen = expanded === mf.month;
          return (
            <div key={mf.month} style={{
              flex: '0 0 calc((100% - 44px) / 12)', minWidth: 44,
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              cursor: 'pointer', padding: '0 2px',
            }}
              onClick={() => setExpanded(isOpen ? null : mf.month)}
            >
              {/* Barometer */}
              <Barometer score={mf.score} element={elem} size={44} />
              {/* Bar */}
              <div style={{ width: '70%', height: 40, display: 'flex', alignItems: 'flex-end', marginTop: 2 }}>
                <div style={{
                  width: '100%',
                  height: `${Math.max(mf.score, 8)}%`,
                  borderRadius: '3px 3px 0 0',
                  background: isOpen ? C.gold : ec.main,
                  opacity: isOpen ? 1 : 0.75,
                  transition: 'all .25s',
                  boxShadow: mf.score >= 70 ? `0 0 8px ${ec.glow}` : 'none',
                }} />
              </div>
              {/* Month label */}
              <div style={{ fontSize: 10, color: isOpen ? C.gold : C.mist4, marginTop: 3, fontWeight: isOpen ? 700 : 400 }}>
                {isZh ? `${MONTH_ZH[mf.month - 1]}月` : mf.month}
              </div>
              {/* Element dot */}
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: ec.main, marginTop: 2, opacity: 0.7 }} />
            </div>
          );
        })}
      </div>

      {/* Month cards grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 12 }}>
        {monthlyFortune_.map((mf) => {
          const qualityKey   = isZh ? mf.quality : (mf.quality_en || mf.quality);
          const qs           = QUALITY_STYLE[qualityKey] || QUALITY_STYLE['平'];
          const open         = expanded === mf.month;
          const elem         = getStemElement(mf.ganzhi);
          const ec           = ELEMENT_COLOR[elem] || ELEMENT_COLOR.Earth;
          const displayQuality = isZh ? mf.quality : (mf.quality_en || mf.quality);
          const displayTrend   = t(mf, 'energy_trend');
          const displaySummary = t(mf, 'summary');
          const displayAdvice  = t(mf, 'advice');
          const displayEvents  = isZh ? mf.key_events : (mf.key_events_en || mf.key_events);

          return (
            <div key={mf.month} onClick={() => setExpanded(open ? null : mf.month)} style={{
              background: open ? 'rgba(255,255,255,.94)' : 'rgba(255,255,255,.78)',
              border: `1px solid ${open ? C.gold : qs.border}`,
              borderLeft: `3px solid ${open ? C.gold : ec.main}`,
              borderRadius: 10, padding: '14px 16px',
              cursor: 'pointer',
              boxShadow: open ? `0 4px 20px rgba(212,175,55,.18)` : '0 1px 4px rgba(0,0,0,.04)',
              transition: 'all .2s',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <span style={{ fontWeight: 700, fontSize: 15, color: C.mist }}>
                    {isZh ? `${MONTH_ZH[mf.month-1]}月` : `Month ${mf.month}`}
                  </span>
                  <span style={{ marginLeft: 8, fontSize: 13, color: C.mist4 }}>{mf.ganzhi}</span>
                  {/* Element badge */}
                  <span style={{
                    marginLeft: 8, fontSize: 10, color: ec.main,
                    background: ec.glow, borderRadius: 4, padding: '1px 6px',
                    fontWeight: 600,
                  }}>
                    {isZh ? ({Fire:'火',Wood:'木',Water:'水',Earth:'土',Metal:'金'}[elem]) : elem}
                  </span>
                </div>
                <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <Barometer score={mf.score} element={elem} size={38} />
                  <div style={{ fontSize: 11, color: qs.text, fontWeight: 600, marginTop: 2 }}>{displayQuality}</div>
                </div>
              </div>
              <div style={{ fontSize: 11, color: C.mist4, marginTop: 4, marginBottom: 6 }}>{mf.solar_term}</div>
              <div style={{ fontSize: 12, color: C.mist3, marginBottom: 4, lineHeight: 1.65 }}>{displaySummary}</div>
              <div style={{ fontSize: 12, color: qs.text, fontWeight: 500 }}>{displayTrend}</div>
              {/* v2.2 alert badges */}
              {(mf.element_alert && mf.element_alert.severity === 'extreme') && (
                <div style={{marginTop:4,fontSize:10,fontWeight:700,letterSpacing:'.04em',
                  color:'#DC2626',background:'rgba(220,38,38,.08)',border:'1px solid rgba(220,38,38,.2)',
                  borderRadius:6,padding:'2px 8px',display:'inline-block'}}>
                  ⚡ {isZh ? '极端预警' : 'Extreme Alert'}
                </div>
              )}
              {(mf.penalty_alerts && mf.penalty_alerts.length > 0) && (
                <div style={{marginTop:4,fontSize:10,fontWeight:700,letterSpacing:'.04em',
                  color:'#D4AF37',background:'rgba(212,175,55,.08)',border:'1px solid rgba(212,175,55,.25)',
                  borderRadius:6,padding:'2px 8px',display:'inline-block',marginLeft:4}}>
                  🛡 {isZh ? '深度护持' : 'Deep Protection'}
                </div>
              )}

              {open && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid rgba(212,175,55,.18)` }}>
                  <div style={{ fontSize: 12, marginBottom: 6 }}>
                    <span style={{ color: C.emerald, fontWeight: 600 }}>{luckyLabel}: </span>
                    <span style={{ color: C.mist3 }}>{mf.lucky_days.join('、')}</span>
                  </div>
                  <div style={{ fontSize: 12, marginBottom: 6 }}>
                    <span style={{ color: '#DC2626', fontWeight: 600 }}>{cautionLabel}: </span>
                    <span style={{ color: C.mist3 }}>{mf.caution_days.join('、')}</span>
                  </div>
                  <div style={{ fontSize: 12, marginBottom: 8 }}>
                    <span style={{ fontWeight: 600, color: C.mist2 }}>{focusLabel}: </span>
                    <span style={{ color: C.mist3 }}>{(displayEvents || []).join(' · ')}</span>
                  </div>
                  <div style={{ fontSize: 12, background: C.emeraldDim || 'rgba(6,78,59,.07)', border: '1px solid rgba(6,78,59,.14)', borderRadius: 6, padding: '8px 12px', color: C.emerald, lineHeight: 1.65 }}>
                    {displayAdvice}
                  </div>

                  {/* element_alert amber box */}
                  {mf.element_alert && mf.element_alert.severity && (
                    <div style={{marginTop:10, background:'rgba(212,175,55,.08)', border:'1px solid rgba(220,38,38,.25)', borderLeft:'3px solid #DC2626', borderRadius:6, padding:'8px 12px'}}>
                      <div style={{fontSize:11,fontWeight:700,color:'#DC2626',marginBottom:4,letterSpacing:'.03em'}}>
                        ⚡ {isZh ? '元素过载警告' : 'Element Overload Alert'}
                      </div>
                      <div style={{fontSize:12,color:'#7C2D12',lineHeight:1.65}}>
                        {isZh ? mf.element_alert.text_zh : mf.element_alert.text_en}
                      </div>
                    </div>
                  )}

                  {/* penalty_alerts amber boxes */}
                  {mf.penalty_alerts && mf.penalty_alerts.length > 0 && (
                    <div style={{marginTop:8}}>
                      {mf.penalty_alerts.map((pa, i) => (
                        <div key={i} style={{marginTop:i>0?6:0, background:'rgba(212,175,55,.07)', border:'1px solid rgba(212,175,55,.3)', borderLeft:`3px solid ${pa.type==='相冲'?'#D4AF37':'#B45309'}`, borderRadius:6, padding:'8px 12px'}}>
                          <div style={{fontSize:11,fontWeight:700,color: pa.type==='相冲'?'#D4AF37':'#B45309',marginBottom:4,letterSpacing:'.03em'}}>
                            {pa.type==='相冲' ? '〰' : '🛡'} {isZh ? pa.type : (pa.type==='相冲'?'Branch Clash':'Branch Penalty')} · {pa.pillar_zh}
                          </div>
                          <div style={{fontSize:12,color:'#78350F',lineHeight:1.65}}>
                            {isZh ? pa.text_zh : pa.text_en}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MonthlyTimeline;
